import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import { Form, Formik } from 'formik';
import InputField from '../../../../../common/InputField';
import { addressFormSchema } from '../../../../../schemas/addressFormSchema';
import statesAndCityData from './../../../../../services/dbs/state_and_cities.json'
import { SET_SHIPPING_ADDRESS } from '../../../../../store/types/actions/Order.actions';

interface AddressesProps {
    gotoNext: () => void;
}

interface StateAndDistType {
    state: string; 
    districts: Array<string>
}

type CountryType = {
    name: string;
    code: string;
}

interface AddressesState {
    fullName: string;
    phone: string;
    postalCode: string;
    street_area_dist: string;
    landmark: string;
    state: string;
    country: string;
    alternatePhone: string;
}

const Addresses = (props: AddressesProps) => {
    const [showAddressForm, setShowAddressForm] = useState(false)
    const [values, setValues] = useState<AddressesState>({
        fullName: '',
        phone: '',
        postalCode: '',
        street_area_dist: '',
        landmark: '',
        state: '',
        country: 'IN',
        alternatePhone: ''
    })
    
    const [city, setCity] = useState('')
    const [cities, setCities] = useState<Array<string>>([])
    const countryList: Array<CountryType> = [{ "name": "India", "code": "IN" }]
    const dispatch = useDispatch()

    useEffect(() => {
        const filteredStateAndCities: Array<StateAndDistType> = 
            statesAndCityData.states
            .filter((sc: StateAndDistType) => 
                sc.state === values.state
            )
            if(filteredStateAndCities && filteredStateAndCities.length > 0) {
                setCities(filteredStateAndCities[0].districts)
                setCity(filteredStateAndCities[0].districts[0])
            }
    }, [values.state])
    
    const submitHandler = (data: any, setSubmitting: any) => {
        setSubmitting(true)
        const { street_area_dist, locality, landmark, ...rest } = data
        dispatch({
            type: SET_SHIPPING_ADDRESS ,
            payload: { 
                ...rest,
                address: `${street_area_dist}, ${landmark}`, 
                state: values.state,
                city: city
            }
        })
        props.gotoNext()
        setSubmitting(false)
    }

    const onSelectCity = (event: SelectChangeEvent) => {
        const { value } = event.target
        setCity(value)
    }

    const onSelectChange = (event: SelectChangeEvent) => {
		const { name, value } = event.target
        setValues((prevState) => {
            return {
                ...prevState,
                [name]: value
            }
        })
	}
    
    return (
        <>
            {!showAddressForm && <Box display="flex" justifyContent="flex-start">
                <Button color="primary" variant="contained" onClick={() => setShowAddressForm(true)} startIcon={<AddIcon />}>
                    Add New Address
                </Button>
            </Box>}
            <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
                <Grid item sm={0} md={0}></Grid>
                <Grid item sm={12} md={12}>
                    {showAddressForm && <Box display="flex" flexDirection="column" justifyContent='center'>
                        <Box component="h4"> New Address Form </Box>
                        <Paper>
                            <Box flex={1} display='flex' p={4} flexDirection='column'>
                                <Formik
                                    validateOnChange={true}
                                    initialValues={{ ...values }}
                                    validationSchema={addressFormSchema}
                                    onSubmit={(data, { setSubmitting }) => submitHandler(data, setSubmitting)}>
                                    {({ isSubmitting }) => (
                                        <Form noValidate autoComplete="off">
                                            <Box mb={{ xs: 2, xl: 3 }}>
                                                <InputField
                                                    required
                                                    placeholder='Full Name'
                                                    name='fullName'
                                                    label='Full Name'
                                                    variant='outlined'
                                                />
                                            </Box>
                                            <Box mb={{ xs: 2, xl: 3 }}>
                                                <InputField
                                                    required
                                                    name='phone'
                                                    label='Phone'
                                                    variant='outlined'
                                                    placeholder='10 digit phone number'
                                                />
                                            </Box>
                                            <Box mb={{ xs: 2, xl: 3 }}>
                                                <InputField
                                                    required
                                                    name='postalCode'
                                                    label='Pincode'
                                                    variant='outlined'
                                                />
                                            </Box>
                                            <Box mb={{ xs: 2, xl: 3 }}>
                                                <InputField
                                                    name='street_area_dist'
                                                    multiline={true}
                                                    rows={2}
                                                    label='Street/Area/District'
                                                    variant='outlined'
                                                />
                                            </Box>
                                            <Box mb={{ xs: 2, xl: 3 }}>
                                                <InputField
                                                    required
                                                    name='landmark'
                                                    label='Landmark (Optional)'
                                                    variant='outlined'
                                                />
                                            </Box>
                                            <Box mb={{ xs: 2, xl: 3 }}>
                                                <FormControl fullWidth variant="outlined">
                                                    <InputLabel id="state-label">Select State</InputLabel>
                                                    <Select
                                                        labelId="state-label"
                                                        id="state-list"
                                                        name="state"
                                                        label="Select State"
                                                        defaultValue={values.state}
                                                        onChange={onSelectChange}
                                                    >
                                                        {statesAndCityData.states && statesAndCityData.states.map((stateObj: StateAndDistType) => {
                                                            return <MenuItem key={stateObj.state} value={stateObj.state}>{stateObj.state}</MenuItem>
                                                        })}
                                                    </Select>
                                                </FormControl>
                                            </Box>
                                            <Box mb={{ xs: 2, xl: 3 }}>
                                                <FormControl fullWidth variant="outlined">
                                                    <InputLabel id="city-label">Select City</InputLabel>
                                                    <Select
                                                        labelId="city-label"
                                                        id="cities-list"
                                                        name="city"
                                                        label="Select City"
                                                        defaultValue={city}
                                                        onChange={onSelectCity}
                                                    >
                                                        {cities && cities.length > 0 && cities.map((city: string) => {
                                                            return <MenuItem key={city} value={city}>{city}</MenuItem>
                                                        })}
                                                    </Select>
                                                </FormControl>
                                            </Box>
                                            <Box mb={{ xs: 2, xl: 3 }}>
                                                <FormControl fullWidth variant="outlined">
                                                    <InputLabel id="country-label">Select Country</InputLabel>
                                                    <Select
                                                        labelId="country-label"
                                                        id="country-list"
                                                        name="country"
                                                        label="Select Country"
                                                        defaultValue={values.country}
                                                        onChange={onSelectChange}
                                                    >
                                                        {countryList.map((country: CountryType) => {
                                                            return <MenuItem key={country.code} value={country.code}>{country.name}</MenuItem>
                                                        })}
                                                    </Select>
                                                </FormControl>
                                            </Box>
                                            <Box mb={{ xs: 2, xl: 3 }}>
                                                <InputField
                                                    required
                                                    name='alternatePhone'
                                                    label='Alternate Phone (Optional)'
                                                    variant='outlined'
                                                />
                                            </Box>
                                            <Box
                                                mt={4}
                                                display='flex'
                                                flexDirection={{ xs: 'column', sm: 'row' }}
                                                justifyContent='space-between'>
                                                <Button
                                                    color="secondary"
                                                    variant="outlined"
                                                    sx={{ mb: 1}}
                                                    onClick={() => setShowAddressForm(false)}
                                                >
                                                    Cancel
                                                </Button>
                                                <Button
                                                    color="primary"
                                                    variant="contained"
                                                    type="submit"
                                                    disabled={isSubmitting}>
                                                    Save and Deliver Here
                                                </Button>
                                            </Box>
                                        </Form>
                                    )}
                                </Formik>
                            </Box>
                        </Paper>
                    </Box>
                }
                </Grid>
                <Grid item sm={0} md={0}></Grid>
            </Grid>
        </>
    )
}

export  default Addresses;
